import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, BadgeCheck, Banknote, BarChart3, Building2, ChevronDown, ExternalLink, FileText,
  Globe, Layers, Loader2, Mail, MapPin, Phone, ScrollText, ShieldAlert, Users,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import ProcurementAccessGate from '../components/ProcurementAccessGate';
import { useProcurementAccess } from '../context/ProcurementAccessContext';
import Pagination from '../components/Pagination';
import { fmtAmount, fmtDate } from '../api/lots';
import {
  asCodeList, getSupplierAnalytics, getSupplierContracts, getSupplierDetail, getSupplierParticipations,
  SupplierAnalyticsResponse, SupplierContract, SupplierDetailResponse, SupplierParticipation,
} from '../api/suppliers';

const PARTS_PAGE = 20;

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  if (value == null || value === '' || value === '—') return null;
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-4 py-2 border-b border-gray-50 last:border-0">
      <dt className="sm:w-64 shrink-0 text-xs font-bold text-gray-400">{label}</dt>
      <dd className="text-sm text-gray-800">{value}</dd>
    </div>
  );
}

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
      <div className="flex items-center gap-2.5 mb-4">
        <span className="w-9 h-9 rounded-xl bg-goszakup-50 text-goszakup-600 flex items-center justify-center">{icon}</span>
        <h2 className="font-extrabold text-gray-900">{title}</h2>
      </div>
      {children}
    </div>
  );
}

export default function SupplierDetailPage() {
  return <ProcurementAccessGate feature="suppliers"><SupplierDetailContent /></ProcurementAccessGate>;
}
function SupplierDetailContent() {
  const { allowed } = useProcurementAccess();
  const canAnalyze = allowed('supplier_analytics');
  const { bin } = useParams<{ bin: string }>();
  const { t, language } = useLanguage();
  const [data, setData] = useState<SupplierDetailResponse | null>(null);
  const [analytics, setAnalytics] = useState<SupplierAnalyticsResponse | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [parts, setParts] = useState<SupplierParticipation[]>([]);
  const [partsTotal, setPartsTotal] = useState(0);
  const [partsPage, setPartsPage] = useState(0);
  const [partsLoading, setPartsLoading] = useState(true);
  const [partsOpen, setPartsOpen] = useState(false);
  const [partsKind, setPartsKind] = useState<'submitted' | 'won'>('submitted');
  const [partsError, setPartsError] = useState(false);
  const [partCounts, setPartCounts] = useState<{ submitted?: number; won?: number }>({});
  const [contractsOpen, setContractsOpen] = useState(false);
  const [contracts, setContracts] = useState<SupplierContract[]>([]);
  const [contractsTotal, setContractsTotal] = useState(0);
  const [contractsPage, setContractsPage] = useState(0);
  const [contractsStatus, setContractsStatus] = useState<'active' | 'executed'>('active');
  const [contractsYear, setContractsYear] = useState('');
  const [contractsYears, setContractsYears] = useState<number[]>([]);
  const [contractsLoading, setContractsLoading] = useState(true);
  const [contractsError, setContractsError] = useState(false);
  const [contractsPartial, setContractsPartial] = useState(false);

  useEffect(() => {
    if (!bin) { setNotFound(true); setLoading(false); return; }
    setLoading(true);
    setNotFound(false);
    setPartsPage(0);
    setPartsOpen(false);
    setPartsKind('submitted');
    setPartCounts({});
    setAnalytics(null);
    setContractsOpen(false);
    setContractsPage(0);
    setContractsStatus('active');
    setContractsYear('');
    let cancelled = false;
    getSupplierDetail(bin)
      .then((result) => { if (!cancelled) setData(result); })
      .catch(() => { if (!cancelled) setNotFound(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    setAnalyticsLoading(canAnalyze);
    if (canAnalyze) getSupplierAnalytics(bin)
      .then((result) => { if (!cancelled) setAnalytics(result); })
      .catch(() => { if (!cancelled) setAnalytics(null); })
      .finally(() => { if (!cancelled) setAnalyticsLoading(false); });
    return () => { cancelled = true; };
  }, [bin, canAnalyze]);

  useEffect(() => {
    if (!bin || !canAnalyze || !partsOpen) return;
    const controller = new AbortController();
    setPartsLoading(true);
    setPartsError(false);
    getSupplierParticipations(bin, { skip: partsPage * PARTS_PAGE, limit: PARTS_PAGE, kind: partsKind }, controller.signal)
      .then((r) => {
        if (!controller.signal.aborted) {
          setParts(r.items); setPartsTotal(r.total);
          // A complete total is independent of the current page. Never replace it with a capped sample.
          if (!r.total_capped && Number.isFinite(r.total) && r.total >= 0) {
            setPartCounts((counts) => ({ ...counts, [partsKind]: r.total }));
          }
        }
      })
      .catch(() => { if (!controller.signal.aborted) { setParts([]); setPartsTotal(0); setPartsError(true); } })
      .finally(() => { if (!controller.signal.aborted) setPartsLoading(false); });
    return () => controller.abort();
  }, [bin, canAnalyze, partsPage, partsOpen, partsKind]);

  useEffect(() => {
    if (!bin || !canAnalyze || !contractsOpen) return;
    const controller = new AbortController();
    setContractsLoading(true);
    setContractsError(false);
    getSupplierContracts(bin, {
      skip: contractsPage * PARTS_PAGE, limit: PARTS_PAGE, status: contractsStatus,
      year: contractsYear ? Number(contractsYear) : undefined,
    }, controller.signal)
      .then((r) => {
        if (!controller.signal.aborted) {
          setContracts(r.items); setContractsTotal(r.total); setContractsYears(r.years); setContractsPartial(!!r.partial);
        }
      })
      .catch(() => { if (!controller.signal.aborted) { setContracts([]); setContractsError(true); } })
      .finally(() => { if (!controller.signal.aborted) setContractsLoading(false); });
    return () => controller.abort();
  }, [bin, canAnalyze, contractsOpen, contractsPage, contractsStatus, contractsYear]);

  const pick = (ru?: string | null, kk?: string | null) => (language === 'kk' ? kk || ru : ru || kk) || '—';
  const count = (value?: number | null) => value == null ? '—' : value.toLocaleString(language === 'kk' ? 'kk-KZ' : 'ru-RU');
  const submittedTotal = partCounts.submitted ?? analytics?.applications;
  const winsTotal = partCounts.won ?? analytics?.wins;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-gray-400">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center text-gray-400 px-4">
        <Building2 className="w-14 h-14 opacity-30 mb-4" />
        <p className="font-bold text-gray-600 mb-1">{t('suppliers.notFound')}</p>
        <Link to="/suppliers" className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-goszakup-600 text-white text-sm font-semibold hover:bg-goszakup-700">
          <ArrowLeft className="w-4 h-4" /> {t('suppliers.backToSearch')}
        </Link>
      </div>
    );
  }

  const s = data.subject;
  const partsPages = Math.ceil(partsTotal / PARTS_PAGE);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Шапка */}
      <div className="bg-gradient-to-b from-white to-slate-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <Link to="/suppliers" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-400 hover:text-goszakup-600 mb-4">
            <ArrowLeft className="w-4 h-4" /> {t('suppliers.backToSearch')}
          </Link>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-xs font-bold text-gray-400">{t('suppliers.table.bin')}: {s.bin || s.iin}</span>
            {data.in_rnu && (
              <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-red-50 text-red-600">
                <ShieldAlert className="w-3.5 h-3.5" /> {t('suppliers.inRnuFull')}
              </span>
            )}
            {!!s.markSmallEmployer && (
              <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700">{t('suppliers.markSmall')}</span>
            )}
            {!!s.qvazi && (
              <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-sky-50 text-sky-700">{t('suppliers.markQvazi')}</span>
            )}
          </div>
          <h1 className="text-xl sm:text-2xl font-extrabold text-gray-900 tracking-tight mb-2">
            {pick(s.nameRu, s.nameKz)}
          </h1>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm text-gray-500">
            {data.regions.length > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="w-4 h-4" />
                {data.regions.map((r) => (language === 'kk' ? r.name_kz || r.name_ru : r.name_ru)).join(', ')}
              </span>
            )}
            {s.email && <span className="inline-flex items-center gap-1.5"><Mail className="w-4 h-4" />{s.email}</span>}
            {s.phone && <span className="inline-flex items-center gap-1.5"><Phone className="w-4 h-4" />{s.phone}</span>}
            {s.website && (
              <span className="inline-flex items-center gap-1.5"><Globe className="w-4 h-4" />{s.website}</span>
            )}
            <a href={data.goszakup_url} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 text-goszakup-600 hover:text-goszakup-700 font-semibold">
              {t('lots.detail.onGoszakup')} <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Регистрационные данные */}
        <Section icon={<FileText className="w-5 h-5" />} title={t('suppliers.registryInfo')}>
          <dl>
            <Row label={t('suppliers.fullName')} value={pick(s.fullNameRu, s.fullNameKz)} />
            <Row label={t('suppliers.table.bin')} value={s.bin} />
            <Row label={t('suppliers.iin')} value={s.iin} />
            <Row label={t('suppliers.regdate')} value={fmtDate(s.regdate)} />
            <Row label={t('suppliers.kopf')} value={data.kopf ? (language === 'kk' ? data.kopf.name_kz || data.kopf.name_ru : data.kopf.name_ru) : null} />
            {/* <Row label={t('suppliers.krp')} value={s.krpCode} /> */}
            <Row label={t('suppliers.oked')} value={asCodeList(s.okedList).join(', ') || null} />
            <Row label={t('suppliers.country')} value={s.countryCode} />
            <Row label={t('suppliers.roles')} value={[
              s.supplier ? t('suppliers.roleSupplier') : null,
              s.customer ? t('suppliers.roleCustomer') : null,
              s.organizer ? t('suppliers.roleOrganizer') : null,
            ].filter(Boolean).join(', ') || null} />
            <Row label={t('lots.detail.updated')} value={fmtDate(s.indexDate)} />
          </dl>
          {data.rnu.length > 0 && (
            <div className="mt-4 rounded-xl bg-red-50 border border-red-100 p-4">
              <p className="flex items-center gap-1.5 text-sm font-bold text-red-600 mb-2">
                <ShieldAlert className="w-4 h-4" /> {t('suppliers.rnuHistory')}
              </p>
              <ul className="space-y-1 text-xs text-red-700">
                {data.rnu.map((r, i) => (
                  <li key={i}>{fmtDate(r.startDate)} — {r.endDate ? fmtDate(r.endDate) : t('suppliers.rnuOpen')}</li>
                ))}
              </ul>
            </div>
          )}
        </Section>

        {/* Аналитика GLauncher */}
        <Section icon={<BarChart3 className="w-5 h-5" />} title={t('lots.detail.analytics')}>
          {!canAnalyze ? <ProcurementAccessGate feature="supplier_analytics" compact /> : analyticsLoading ? (
            <div className="flex justify-center py-10 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : !analytics ? (
            <p className="text-sm text-gray-400">{t('lots.detail.analyticsUnavailable')}</p>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
                <button type="button" onClick={() => setContractsOpen((open) => !open)}
                        aria-expanded={contractsOpen} aria-controls="supplier-contracts"
                        className="rounded-xl p-4 text-left bg-slate-50 hover:bg-goszakup-50 transition-colors">
                  <span className="flex items-center gap-1.5 text-[11px] font-bold mb-1 text-gray-500">
                    <ScrollText className="w-4 h-4" />{t('suppliers.contractsCount')}
                    <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${contractsOpen ? 'rotate-180' : ''}`} />
                  </span>
                  <span className="font-extrabold text-gray-900">{analytics.contracts?.toLocaleString('ru-RU') ?? '—'}</span>
                </button>
                {[
                  { label: t('suppliers.contractsSum'), value: analytics.contracts_sum != null ? `${fmtAmount(analytics.contracts_sum)} ₸` : '—', hl: true, icon: <Banknote className="w-4 h-4" /> },
                  { label: t('suppliers.applications'), value: count(submittedTotal), icon: <BadgeCheck className="w-4 h-4" /> },
                  { label: t('suppliers.totalParticipations'), value: count(analytics.participations), icon: <Users className="w-4 h-4" /> },
                  { label: t('suppliers.totalWins'), value: count(winsTotal), icon: <BadgeCheck className="w-4 h-4" /> },
                  { label: t('suppliers.customers'), value: analytics.customers?.toLocaleString('ru-RU') ?? '—', icon: <Users className="w-4 h-4" /> },
                ].map((c, i) => (
                  <div key={i} className={`rounded-xl p-4 ${c.hl ? 'bg-goszakup-600 text-white' : 'bg-slate-50'}`}>
                    <p className={`flex items-center gap-1.5 text-[11px] font-bold mb-1 ${c.hl ? 'text-white/70' : 'text-gray-400'}`}>{c.icon}{c.label}</p>
                    <p className={`font-extrabold ${c.hl ? 'text-white text-lg' : 'text-gray-900'}`}>{c.value}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mb-6">{t('suppliers.historyMetricsHint')}</p>

              {analytics.partial && <p role="status" className="text-xs text-amber-700 mb-4">{t('suppliers.analyticsPartial')}</p>}

              {contractsOpen && (
                <div id="supplier-contracts" className="rounded-xl border border-gray-100 p-4 mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-gray-900">{t('suppliers.contractsTitle')}</h3>
                    <button type="button" onClick={() => setContractsOpen(false)} className="text-sm text-goszakup-600 font-semibold">
                      {t('suppliers.collapse')}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-3 items-center justify-between mb-4">
                    <div role="tablist" aria-label={t('suppliers.contractsTitle')} className="flex gap-2">
                      {(['active', 'executed'] as const).map((status) => (
                        <button key={status} type="button" role="tab" aria-selected={contractsStatus === status}
                                onClick={() => { setContractsStatus(status); setContractsPage(0); }}
                                className={`px-3 py-2 rounded-lg text-sm font-semibold ${contractsStatus === status ? 'bg-goszakup-600 text-white' : 'bg-slate-100 text-gray-600'}`}>
                          {t(`suppliers.contracts${status === 'active' ? 'Active' : 'Executed'}`)}
                        </button>
                      ))}
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                      {t('suppliers.contractYear')}
                      <select value={contractsYear} onChange={(e) => { setContractsYear(e.target.value); setContractsPage(0); }}
                              className="border border-gray-200 rounded-lg px-3 py-2 bg-white">
                        <option value="">{t('suppliers.allYears')}</option>
                        {contractsYears.map((year) => <option key={year} value={year}>{year}</option>)}
                      </select>
                    </label>
                  </div>
                  {!contractsLoading && !contractsError && contractsPartial && (
                    <p role="status" className="text-xs text-amber-700 mb-3">{t('suppliers.contractsPartial')}</p>
                  )}
                  {contractsLoading ? (
                    <div className="flex justify-center py-6 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
                  ) : contractsError ? (
                    <p role="alert" className="text-sm text-red-600">{t('suppliers.contractsError')}</p>
                  ) : !contracts.length ? (
                    <p className="text-sm text-gray-400">{t('suppliers.noContracts')}</p>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[500px]">
                          <thead><tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                            <th className="px-2 py-2">{t('suppliers.contractNumber')}</th>
                            <th className="px-2 py-2">{t('suppliers.contractDate')}</th>
                            <th className="px-2 py-2">{t('lots.table.status')}</th>
                            <th className="px-2 py-2 text-right">{t('suppliers.contractAmount')}</th>
                          </tr></thead>
                          <tbody>{contracts.map((contract) => (
                            <tr key={contract.id} className="border-b border-gray-50 last:border-0">
                              <td className="px-2 py-2.5">
                                <a href={contract.url} target="_blank" rel="noopener noreferrer" className="font-semibold text-goszakup-600 hover:underline">
                                  {contract.number || contract.id} <ExternalLink className="inline w-3 h-3" />
                                </a>
                              </td>
                              <td className="px-2 py-2.5 whitespace-nowrap">{fmtDate(contract.sign_date)}</td>
                              <td className="px-2 py-2.5">{pick(contract.status_ru, contract.status_kz)}</td>
                              <td className="px-2 py-2.5 text-right whitespace-nowrap">{contract.sum != null ? `${fmtAmount(contract.sum)} ₸` : '—'}</td>
                            </tr>
                          ))}</tbody>
                        </table>
                      </div>
                      {contractsTotal > PARTS_PAGE && (
                        <div className="mt-4"><Pagination currentPage={contractsPage + 1}
                          totalPages={Math.ceil(contractsTotal / PARTS_PAGE)} onPageChange={(p) => setContractsPage(p - 1)} /></div>
                      )}
                    </>
                  )}
                </div>
              )}

              {analytics.top_categories.length > 0 && (
                <>
                  <h3 className="font-extrabold text-gray-900 mb-1">{t('suppliers.topCategories')}</h3>
                  <p className="text-xs text-gray-400 mb-3">{t('suppliers.topCategoriesHint')}</p>
                  <div className="space-y-2">
                    {analytics.top_categories.map((c) => (
                      <Link
                        key={c.code}
                        to={`/lots?enstru=${encodeURIComponent(c.code)}&cat_label=${encodeURIComponent(pick(c.name_ru, c.name_kz))}`}
                        className="flex items-center gap-3 rounded-xl border border-gray-100 p-3 hover:border-goszakup-200 hover:bg-goszakup-50/40 transition-colors"
                      >
                        <span className="w-8 h-8 rounded-lg bg-goszakup-50 text-goszakup-600 flex items-center justify-center shrink-0">
                          <Layers className="w-4 h-4" />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-semibold text-gray-800 line-clamp-1">{pick(c.name_ru, c.name_kz) !== '—' ? pick(c.name_ru, c.name_kz) : c.code}</span>
                          <span className="block text-[11px] text-gray-400">
                            {c.code}{c.kpved_section_name || c.kpved_section_name_kz ? ` · ${pick(c.kpved_section_name, c.kpved_section_name_kz)}` : ''}
                          </span>
                        </span>
                        <span className="shrink-0 text-xs font-bold text-gray-500 bg-slate-100 px-2.5 py-1 rounded-full">
                          {c.count.toLocaleString('ru-RU')}
                        </span>
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </Section>

        {/* Поданные заявки (участия) */}
        <ProcurementAccessGate feature="supplier_analytics" compact>
        <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
          <h2>
            <button type="button" onClick={() => setPartsOpen((open) => !open)} aria-expanded={partsOpen}
                    aria-controls="supplier-applications" title={t('suppliers.applicationCountsHint')}
                    className="flex items-center gap-2.5 w-full text-left font-extrabold text-gray-900">
              <span className="w-9 h-9 rounded-xl bg-goszakup-50 text-goszakup-600 flex items-center justify-center"><ScrollText className="w-5 h-5" /></span>
              {t('suppliers.applicationsTitle')}{' '}
              <span className="text-gray-500 text-sm">({count(submittedTotal)})</span>
              <ChevronDown className={`w-5 h-5 ml-auto transition-transform ${partsOpen ? 'rotate-180' : ''}`} />
            </button>
          </h2>
          {partsOpen && <div id="supplier-applications" className="mt-4">
            <div role="tablist" aria-label={t('suppliers.applicationsTitle')} className="flex gap-2 mb-3">
              {(['submitted', 'won'] as const).map((kind) => (
                <button key={kind} type="button" role="tab" aria-selected={partsKind === kind}
                        onClick={() => { setPartsKind(kind); setPartsPage(0); }}
                        className={`px-3 py-2 rounded-lg text-sm font-semibold ${partsKind === kind ? 'bg-goszakup-600 text-white' : 'bg-slate-100 text-gray-600'}`}>
                  {t(kind === 'submitted' ? 'suppliers.participations' : 'suppliers.winningApplications')}{' '}
                  <span className="ml-1">({count(kind === 'submitted' ? submittedTotal : winsTotal)})</span>
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mb-3">{t('suppliers.applicationCountsHint')}</p>
            {partsKind === 'won' && <p className="text-xs text-gray-500 mb-3">{t('suppliers.winningHint')}</p>}
          {partsLoading ? (
            <div className="flex justify-center py-10 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : partsError ? (
            <p role="alert" className="text-sm text-red-600">{t('suppliers.participationsError')}</p>
          ) : parts.length === 0 ? (
            <p className="text-sm text-gray-400">{t('suppliers.noParticipations')}</p>
          ) : (
            <>
              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-sm min-w-[760px]">
                  <thead>
                    <tr className="text-left text-[11px] font-bold text-gray-400 uppercase border-b border-gray-100">
                      <th className="px-2 py-2">{t('suppliers.partAnno')}</th>
                      <th className="px-2 py-2">{t('suppliers.partDate')}</th>
                      <th className="px-2 py-2">{t('lots.table.status')}</th>
                      <th className="px-2 py-2 text-right">{t('lots.detail.totalSum')}</th>
                      <th className="px-2 py-2">{t('suppliers.partLots')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parts.map((p) => (
                      <tr key={`${p.buy_id}-${p.app_id}`} className="border-b border-gray-50 last:border-0">
                        <td className="px-2 py-2.5">
                          <span className="font-semibold text-gray-800 line-clamp-2">{pick(p.anno_name_ru, p.anno_name_kz)}</span>
                          {p.anno_number && <span className="block text-[11px] text-gray-400">№ {p.anno_number}</span>}
                        </td>
                        <td className="px-2 py-2.5 text-gray-600 whitespace-nowrap">{fmtDate(p.date_apply)}</td>
                        <td className="px-2 py-2.5 text-gray-600">
                          {(language === 'kk' ? p.buy_status_kz || p.buy_status_ru : p.buy_status_ru) || '—'}
                        </td>
                        <td className="px-2 py-2.5 text-right font-bold text-gray-900 whitespace-nowrap">
                          {p.total_sum != null ? `${fmtAmount(p.total_sum)} ₸` : '—'}
                        </td>
                        <td className="px-2 py-2.5">
                          <span className="flex flex-wrap gap-1">
                            {(p.lots?.length ? p.lots : p.lot_ids.map((id) => ({ id, number: null }))).map((lot) => (
                              <Link key={lot.id} to={`/lots/${lot.id}`} target="_blank" rel="noopener noreferrer"
                                    className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-goszakup-50 text-goszakup-700 hover:bg-goszakup-100">
                                {lot.number || lot.id}
                              </Link>
                            ))}
                            {!p.lots?.length && !p.lot_ids.length && <span className="text-xs text-gray-400">{t('suppliers.lotDataUnavailable')}</span>}
                          </span>
                          {p.lots_incomplete && <p className="text-xs text-amber-700 mt-1">{t('suppliers.lotsIncomplete')}</p>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {partsPages > 1 && (
                <div className="mt-5">
                  <Pagination
                    currentPage={partsPage + 1}
                    totalPages={partsPages}
                    onPageChange={(p) => setPartsPage(p - 1)}
                  />
                </div>
              )}
            </>
          )}
          </div>}
        </section>
        </ProcurementAccessGate>
      </div>
    </div>
  );
}
